// src/data/editions/responsible-gaming.js
//
// RESPONSIBLE GAMING edition: a publicly funded program, not an employer.
// The people in the data are members of the public who signed up privately
// and picked a region; the console is read by the program and its funders
// (addictions services, the gaming regulator) to see reach and risk by region
// and to place resources. So: no leave forecast, no ROI, no absence or
// turnover; the organizational leg is the program's own delivery figures
// (helpline contacts, counselling intakes, self-exclusion enrolments, waits)
// and the headline risk is the gambling screen, not Running Hot. DRAFT copy.

const responsible_gaming = {
    id: 'responsible_gaming',
    label: 'Responsible Gaming — Public Program',

    palette: { deep: '#2B1A4D', blue: '#4A2C82', yellow: '#E8B04B' },

    // The console as a page: the hub sidebar entry, the console sidebar
    // kicker, the header line, and the tabs that make no sense for a program.
    consoleLabel: 'Program Insights',
    headerSubtitle: 'Reach and risk by region, built from private check-ins, the program\'s own figures and sector references.',
    hiddenTabs: ['cc-predictive', 'cc-roi'],
    // The members' leg leads with this screen instead of Running Hot.
    populationRisk: { metric: 'gambling', label: 'at risk', detail: 'gambling screen 8+', moderateLabel: 'moderate', moderateDetail: 'gambling screen 3 to 7' },

    pageTitles: {
        'cc-overview': 'Reach & Risk Overview',
        'cc-triangulation': 'Triangulation: People, the Program, the Average',
        'cc-data': 'Data Intake: The Program’s Own Figures',
        'cc-ai': 'AI Intelligence & Charting',
        'cc-readiness': 'Strain by Region',
        'cc-predictive': 'Outlook',
        'cc-roi': 'Program Cost & Impact',
        'cc-feedback': 'Anonymous Pulse & Sentiment',
        default: 'Program Insights'
    },

    trendSignalLabel: 'Help-seeking signal',

    summary: {
        source: 'demo',
        capacityScore: 74,
        capacityTrend: 1,
        workforce: 1800,
        highSignalCohort: 210,
        topPriorityGroup: 'Edmonton & Area',
        topPulseTheme: 'Money stress',
        participationRate: 41,
        modeledClaimsAvoidance: '—',
        modeledStdClaims: { low: 0, high: 0, windowDays: 90 },
        modeledLtdTransition: { low: 0, high: 0 },
        leaveProbability: { mid: 0, low: 0, high: 0 },
        outlookTitle: 'Help-seeking outlook',
        outlookNoun: 'referrals',
        outlookUnit: 'referrals',
        financialNoun: 'program cost avoidance'
    },

    capacityTrend: [
        { date: '1st', capacity: 73, signal: 12 },
        { date: '4th', capacity: 73, signal: 13 },
        { date: '8th', capacity: 72, signal: 14 },
        { date: '12th', capacity: 74, signal: 12 },
        { date: '16th', capacity: 74, signal: 12 },
        { date: '20th', capacity: 75, signal: 11 },
        { date: '24th', capacity: 74, signal: 12 },
        { date: '28th', capacity: 74, signal: 11 },
        { date: 'Today', capacity: 74, signal: 11 }
    ],

    readiness: {
        scoreLabel: 'Population Wellbeing Score',
        signalSubtext: 'money stress + play frequency',
        groups: [
            { name: 'Edmonton & Area', pressure: 34, note: 'Highest moderate-risk share; online play rising', responses: 520 },
            { name: 'Calgary & Area', pressure: 31, note: 'Steady; sports betting the most-cited form', responses: 480 },
            { name: 'Northern Alberta', pressure: 36, note: 'Fewest services within an hour', responses: 160 },
            { name: 'Central Alberta', pressure: 28, note: 'Within normal range', responses: 140 },
            { name: 'Southern Alberta', pressure: 27, note: 'Within normal range', responses: 110 }
        ],
        signals: [
            { label: 'Self-reported strain trend', value: '+2%', tier: 'Intermediate', tone: 'amber' },
            { label: 'Helpline contacts', value: '+9%', tier: 'Lagging', tone: 'amber' },
            { label: 'Counselling intakes', value: '+6%', tier: 'Lagging', tone: 'emerald' },
            { label: 'Self-exclusion enrolments', value: 'Steady', tier: 'Lagging', tone: 'emerald' }
        ]
    },

    feedback: {
        scopes: [
            { id: 'org', label: 'Province-wide', responses: 410 },
            { id: 'edmonton', label: 'Edmonton & Area', responses: 150 },
            { id: 'calgary', label: 'Calgary & Area', responses: 132 },
            { id: 'north', label: 'Northern Alberta', responses: 48 },
            { id: 'central', label: 'Central Alberta', responses: 41 },
            { id: 'south', label: 'Southern Alberta', responses: 39 }
        ],
        submissions: [
            { id: 1, time: '3 hours ago', theme: 'Money stress', sentiment: 'risk', scope: 'edmonton',
              text: 'The ads are everywhere during the game. I set a limit and the app offered me a bonus to go past it.' },
            { id: 2, time: '6 hours ago', theme: 'Access', sentiment: 'risk', scope: 'north',
              text: 'The nearest counsellor is a two-hour drive. Phone sessions would change everything up here.' },
            { id: 3, time: 'Yesterday', theme: 'Family', sentiment: 'positive', scope: 'calgary',
              text: 'The family page helped me say something to my brother without it turning into a fight.' },
            { id: 4, time: 'Yesterday', theme: 'Tools', sentiment: 'positive', scope: 'central',
              text: 'The self-check told me plainly where I sit. I did not like it, but it was fair.' },
            { id: 5, time: '2 days ago', theme: 'Money stress', sentiment: 'risk', scope: 'edmonton',
              text: 'Payday is the hardest day. Something that reaches me that morning would help more than a pamphlet.' }
        ],
        sentiment: {
            positiveSummary: 'The private self-check and the family page are read as fair and useful.',
            emergingTheme: 'Money stress around payday and in-app bonus prompts are the most frequent risk notes; access is the theme in the north.'
        }
    },

    predictive: {
        title: 'The Help-Seeking Funnel',
        intro: 'In the modeled baseline, a share of people at moderate risk would be expected to seek help within the year, and a smaller share to enrol in counselling. Directional, not a forecast; it needs the program\'s own intake figures beside it before any claim.',
        lever: 'Earlier routing to the helpline',
        ownLabel: 'Counselling intake rate',
        tiers: [
            { value: '1,800', label: 'People reached', source: 'observed', tone: 'neutral', width: 100 },
            { value: '210', label: 'Moderate or higher risk', source: 'observed', tone: 'brand', width: 80 },
            { value: '60-90', label: 'Modeled helpline contacts - qtr', source: 'modeled', tone: 'amber', width: 60 },
            { value: '25-40', label: 'Modeled counselling intakes', source: 'modeled', tone: 'amber', width: 44 },
            { value: '8-14', label: 'Self-exclusion enrolments - tail', source: 'modeled', tone: 'muted', width: 30 }
        ],
        cards: [
            { title: 'Risk-to-contact association', value: '~3x', valueTone: 'amber', level: 'Intermediate', source: 'modeled', dark: false,
              subtext: 'Modeled association - not causal',
              body: 'Regions with a higher moderate-risk share show more helpline contacts per thousand people reached. Directional, and confirmed only against the program\'s own intake figures.' },
            { title: 'Modeled intake probability', value: '14%', valueTone: 'white', level: 'Lagging', source: 'modeled', dark: true,
              subtext: 'Midpoint - 10-18% band',
              body: 'Modeled share of people at moderate or higher risk who enrol in counselling within a quarter. The band narrows as the program\'s own figures are added.' },
            { title: 'Routing lift', value: '+28%', valueTone: 'emerald', level: 'Leading', source: 'observed', dark: false,
              subtext: 'Observed in engaged cohorts - attribution pending',
              body: 'People who used the self-check tapped Talk to Someone more often than those who only read content: a leading indicator, shown as an association.' }
        ],
        footer: {
            lead: 'These are models, not forecasts.',
            note: 'Projections are conservatively attributed and reported by region only, never for a person. Help-seeking is a lagging outcome that needs the program\'s own figures beside it before any causal claim.'
        }
    },

    roi: {
        currency: 'CAD',
        locale: 'en-CA',
        benchSource: 'sector reference',
        replacementMultiple: 0,
        turnoverRate: 0,
        termLabels: {
            organization: 'the program',
            lostTime: 'Earlier help',
            lostTimeBasis: 'Not a cost model: a public program measures reach, risk and help-seeking.'
        }
    },

    copilot: { exampleScope: 'Edmonton & Area' }
};

export default responsible_gaming;
